export interface GoogleAnalyticsOptions {
  gtmID: string;
  debug?: boolean;
}

export type AnalyticsEventCategory = 'event' | 'exception';

type GAEventType = 'event' | 'config' | 'js';

export class GoogleAnalytics {
  options: GoogleAnalyticsOptions;

  constructor(opts: GoogleAnalyticsOptions) {
    this.options = opts;

    const { gtmID, debug } = opts;

    if (gtmID === undefined) {
      throw new Error('GA cannot be initialized! gtmID mandatory.');
    }

    if (typeof window !== 'undefined') {
      const hasSrcScript = GoogleAnalytics.hasSrcScript();
      if (!hasSrcScript) {
        GoogleAnalytics.insertSrc(gtmID);
      }

      GoogleAnalytics.insertGtagScript();

      const args: Record<string, unknown> = { page_path: window.location.pathname };
      if (debug) {
        args.debug_mode = true;
      }
      this.push('config', gtmID, args);
    }
  }

  private static readonly hasSrcScript = () => {
    return document.querySelectorAll("script[src^='https://www.googletagmanager.com/gtag']").length > 0;
  };

  private static readonly insertSrc = (gmtID: string) => {
    const srcScript = (gmtID: string) => {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gmtID}`;
      return script;
    };

    document.head.insertBefore(srcScript(gmtID), document.head.childNodes[0]);
  };

  private static readonly insertGtagScript = () => {
    const script = document.createElement('script');
    const replaceGtagFunction = "if (typeof gtag !== 'function') function gtag(){dataLayer.push(arguments);}";
    script.innerText = `window.dataLayer = window.dataLayer || [];\
      ${replaceGtagFunction}\
      gtag('js', new Date());\
      `;

    document.head.insertBefore(script, document.head.childNodes[1]);
  };

  private push(type: GAEventType, key?: string | unknown, args?: Record<string, unknown>) {
    // skipcq JS-0295
    // @ts-expect-error Gtag is defined through script element
    gtag(type, key, { send_to: this.options.gtmID, ...args });
  }

  public trackEvent(category: AnalyticsEventCategory, eventName?: string, name?: string, value?: number, dimensions?: Record<string, unknown>) {
    const args = dimensions ?? {};
    let key = eventName ?? category;

    if (category === 'exception') {
      key = category;
      args.error = eventName;

      // GA4 Expects description to be completed
      if (!dimensions?.description) {
        args.description = eventName;
      }
    }

    if (name) {
      args.event_name = name;
    }

    if (value !== undefined) {
      args.event_value = value;
    }

    this.push('event', key, args);
  }

  public trackRoute(url: string) {
    this.push('event', 'page_view', { page_path: url });
  }
}
